import { useState } from "react";
import { invokeFunction } from "@/integrations/supabase/client";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import {
  ChevronRight,
  ChevronDown,
  Loader2,
  Network,
  GitBranch,
} from "lucide-react";

// ── Types (mirrored from edge function) ──────────────────────────────────────

export interface MindMapNode {
  id: string;
  title: string;
  summary: string;
  content: string | null;
  children: MindMapNode[];
}

export interface MindMapLesson {
  centralTopic: string;
  overview: string;
  nodes: MindMapNode[];
}

// ── Props ────────────────────────────────────────────────────────────────────

interface MindMapLessonViewProps {
  lesson: MindMapLesson;
  skillNodeId: string;
  onLessonUpdated?: (lesson: MindMapLesson) => void;
  readOnly?: boolean;
}

// ── Node card (recursive) ────────────────────────────────────────────────────

function MindMapNodeCard({
  node,
  depth,
  skillNodeId,
  expandingId,
  onExpand,
  expandedIds,
  onToggle,
}: {
  node: MindMapNode;
  depth: number;
  skillNodeId: string;
  expandingId: string | null;
  onExpand: (nodeId: string) => void;
  expandedIds: Set<string>;
  onToggle: (nodeId: string) => void;
}) {
  const isExpanded = expandedIds.has(node.id);
  const isExpanding = expandingId === node.id;
  const hasContent = !!node.content;
  const isTopLevel = depth === 0;

  return (
    <div className={depth > 0 ? "ml-6 border-l-2 border-muted pl-4" : ""}>
      <div
        className={`group rounded-lg border transition-colors ${
          isTopLevel
            ? "bg-card hover:bg-accent/50"
            : "bg-muted/30 hover:bg-muted/50"
        }`}
      >
        <button
          className="w-full text-left p-4 flex items-start gap-3"
          onClick={() => {
            if (hasContent) {
              onToggle(node.id);
            } else {
              onExpand(node.id);
            }
          }}
          disabled={isExpanding}
        >
          <div className="mt-0.5 shrink-0 text-muted-foreground">
            {isExpanding ? (
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            ) : isExpanded ? (
              <ChevronDown className="h-5 w-5" />
            ) : (
              <ChevronRight className="h-5 w-5" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3
              className={`font-semibold ${
                isTopLevel ? "text-base" : "text-sm"
              }`}
            >
              {node.title}
            </h3>
            {!isExpanded && (
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                {node.summary}
              </p>
            )}
            {!hasContent && !isExpanding && (
              <span className="inline-block mt-2 text-xs text-primary/70 group-hover:text-primary transition-colors">
                Click to expand
              </span>
            )}
          </div>
        </button>

        {isExpanded && hasContent && (
          <div className="px-4 pb-4 border-t border-border/50">
            <div className="prose prose-slate dark:prose-invert max-w-none mt-4 text-sm">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {node.content!}
              </ReactMarkdown>
            </div>
          </div>
        )}
      </div>

      {/* Children: always visible as a nested tree */}
      {node.children.length > 0 && (
        <div className="mt-2 space-y-2">
          {node.children.map((child) => (
            <MindMapNodeCard
              key={child.id}
              node={child}
              depth={depth + 1}
              skillNodeId={skillNodeId}
              expandingId={expandingId}
              onExpand={onExpand}
              expandedIds={expandedIds}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export default function MindMapLessonView({
  lesson: initialLesson,
  skillNodeId,
  onLessonUpdated,
  readOnly,
}: MindMapLessonViewProps) {
  const [lesson, setLesson] = useState<MindMapLesson>(initialLesson);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [expandingId, setExpandingId] = useState<string | null>(null);
  const { toast } = useToast();

  const handleToggle = (nodeId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  };

  const handleExpand = async (nodeId: string) => {
    setExpandingId(nodeId);
    try {
      const { data, error } = await invokeFunction("generate-mindmap-lesson", {
        body: { skillNodeId, action: "expand", mindMapNodeId: nodeId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (data?.lesson) {
        setLesson(data.lesson as MindMapLesson);
        onLessonUpdated?.(data.lesson as MindMapLesson);
      }
      setExpandedIds((prev) => new Set(prev).add(nodeId));
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Failed to expand section",
        description: err instanceof Error ? err.message : "Unknown error",
      });
    } finally {
      setExpandingId(null);
    }
  };

  const totalNodes = lesson.nodes.reduce(
    (acc, n) => acc + 1 + n.children.length,
    0,
  );
  const expandedCount = lesson.nodes.reduce(
    (acc, n) =>
      acc + (n.content ? 1 : 0) + n.children.filter((c) => c.content).length,
    0,
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 text-muted-foreground">
        <Network className="h-5 w-5" />
        <span className="text-sm font-medium">
          Mind Map Lesson — {totalNodes} sections
          {expandedCount > 0 && `, ${expandedCount} expanded`}
        </span>
      </div>

      {/* Overview article */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <GitBranch className="h-5 w-5 text-primary" />
            {lesson.centralTopic}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="prose prose-slate dark:prose-invert max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {lesson.overview}
            </ReactMarkdown>
          </div>
        </CardContent>
      </Card>

      {/* Expand all / collapse all */}
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const all = new Set<string>();
            for (const n of lesson.nodes) {
              if (n.content) all.add(n.id);
              for (const c of n.children) if (c.content) all.add(c.id);
            }
            setExpandedIds(all);
          }}
        >
          Expand all loaded
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setExpandedIds(new Set())}
        >
          Collapse all
        </Button>
      </div>

      {/* Mind map tree */}
      <div className="space-y-3">
        {lesson.nodes.map((node) => (
          <MindMapNodeCard
            key={node.id}
            node={node}
            depth={0}
            skillNodeId={skillNodeId}
            expandingId={expandingId}
            onExpand={handleExpand}
            expandedIds={expandedIds}
            onToggle={handleToggle}
          />
        ))}
      </div>
    </div>
  );
}
